import { useState } from 'react';
import { Globe, Plus } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useWorkspaces, type WorkspaceSummary } from '../../../lib/workspaces';
import { addSpaceLink, updateSpaceBinding, type SpaceBindingUpdate } from '../../../lib/space-bindings';
import { manageSections, presenceAddLinkUrl, presenceLinks, presencePrompt, type ManageAction, type ManageRow, type PresencePart } from '../../../lib/presence';
import { useIsPhone } from '../../../lib/use-is-phone';
import { useSitePaneFits } from '../../../lib/use-site-pane';
import { siteRefOf, useSiteEditRunner } from '../site-review/use-site-edit';
import { cardPresence, presenceCard, type PresenceAction, type WebsitePresence } from '../../../lib/presence-checklist';
import { moveTargets, spaceBindingUpdateError, type SpaceBindingsResponse, type StateBadgeTone } from '../../../lib/space-resources';
import type { WorkspaceWebsite } from '../../../lib/workspace-website';
import { useConfirm } from '../../ConfirmDialog';
import { Modal } from '../../Modal';
import { WorkspaceBadge } from '../WorkspaceBadge';
import { Badge, Button, Card, Input, OverflowMenu, TextButton, Toast, Typography, type OverflowMenuAction } from '../ui';
import { PresenceChecklistCard, presenceActionChat } from './PresenceChecklist';
import { PresenceGlyph } from './PresenceGlyph';
import { usePresenceChat } from './use-presence-chat';
import styles from './PresenceManage.module.css';

// The Space's "Online presence" dialog (Manage, design `Manage.dc.html`): suggestions, websites, social profiles,
// listings and reviews, what agents use for this business, and "+ Add website, page or listing". The sections and
// every row's words and actions are `manageSections` (lib/presence.ts, mirrored by both phones' Manage sheets); the
// row menus are the Resources card's (Open · Reconnect · Make main · Move to another Space · Remove), unchanged.
//
// When the server describes the main website (`presence`, `lib/presence-checklist.ts`), its row is drawn as the
// go-professional checklist card (design "B") instead, carrying the same ⋯ menu; without it, the row is today's.

const TONE_CLASS: Record<StateBadgeTone, string> = {
  warning: styles.toneWarning,
  critical: styles.toneCritical,
  quiet: styles.toneQuiet,
};

export function PresenceManage({
  open,
  onClose,
  workspaceId,
  workspaceName,
  website,
  bindings,
  pageName,
  spacePresence,
}: {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  workspaceName: string;
  website: WorkspaceWebsite | null;
  bindings: SpaceBindingsResponse;
  pageName: string;
  /** The Space's presence from the Space list (`headerPresence`) — the card's last fallback (`cardPresence`). */
  spacePresence?: WebsitePresence | null;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const openChat = usePresenceChat(workspaceId, workspaceName);
  const confirm = useConfirm();
  const workspaces = useWorkspaces();
  const [moving, setMoving] = useState<ManageRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const phone = useIsPhone();
  const paneFits = useSitePaneFits();
  const sections = manageSections(bindings, website);
  const mainSiteUrl = presenceLinks(website, bindings).find((l) => l.main)?.url ?? '';
  const card = presenceCard(cardPresence(bindings, spacePresence), { spaceName: workspaceName, siteUrl: mainSiteUrl, phone, paneFits });
  // The site a checklist chat is about: the tile's, else the main WEBSITE row's; none on a Space with no website yet.
  const mainResource = (bindings.resources ?? []).find((r) => r.kind === 'WEBSITE' && r.main === true && r.state !== 'CANDIDATE');
  const chatSite: WorkspaceWebsite | null =
    website ??
    (mainResource
      ? { name: (mainResource.displayName ?? '').trim(), url: mainSiteUrl, metaSiteId: (mainResource.externalScopeId ?? '').trim() }
      : null);
  const runSiteEdit = useSiteEditRunner({ workspaceId, workspaceName, website: chatSite, siteRef: siteRefOf(chatSite) });
  const actOnChecklist = (a: PresenceAction) => {
    click(a.bi, 'presence-checklist');
    if (a.target.kind === 'url' || a.target.kind === 'desktopOnly') return;
    onClose();
    if (runSiteEdit(a)) return;
    if (a.target.kind !== 'chat') return;
    openChat(presenceActionChat(a.target.message, chatSite));
  };
  const others: WorkspaceSummary[] = workspaces.status === 'ready' ? moveTargets(workspaces.workspaces, workspaceId) : [];
  const click = (button: string, componentName?: string) =>
    logPageClickBi({ page_name: pageName, button_name: button }, componentName ? { componentName } : undefined);
  const partText = (part: PresencePart) => ('text' in part ? part.text : t(part.key, part.values));

  const run = async (update: Omit<SpaceBindingUpdate, 'workspaceId'>) => {
    if (busy) return;
    setBusy(true);
    try {
      const error = spaceBindingUpdateError(await updateSpaceBinding({ workspaceId, ...update }));
      if (error) setToast(t(error));
    } finally {
      setBusy(false);
    }
  };

  const act = (row: ManageRow, action: ManageAction) => {
    click(action.bi, `space-resource-${row.kind.toLowerCase()}`);
    if (action.kind === 'CONFIRM' || action.kind === 'DISMISS') {
      void run({ action: action.kind, bindingId: row.bindingId });
      return;
    }
    if (action.chat) {
      onClose();
      openChat(action.chat);
    }
  };

  const submitLink = async () => {
    if (busy) return;
    const url = presenceAddLinkUrl(draft);
    if (!url) {
      setAddError(t('workspace.presence.addLinkInvalid'));
      return;
    }
    click('presence_add_link');
    setBusy(true);
    setAddError(null);
    try {
      const result = await addSpaceLink(workspaceId, url);
      if (result.outcome === 'UPDATED' || result.outcome === 'UNCHANGED') {
        setDraft('');
        setAdding(false);
      } else if (result.outcome === 'REFUSED') {
        setAddError(result.message || t('workspace.resources.errorGeneric'));
      } else {
        setAddError(t(spaceBindingUpdateError(result.outcome) ?? 'workspace.resources.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  };

  const menuFor = (row: ManageRow): OverflowMenuAction[] =>
    row.menu.map(({ action, key }) => {
      const label = t(key);
      const componentName = `space-resource-${row.kind.toLowerCase()}`;
      switch (action) {
        case 'OPEN':
          return { key: action, label, href: row.openUrl, external: true, newTab: true, onSelect: () => click('space_resource_open', componentName) };
        case 'RECONNECT':
          return {
            key: action,
            label,
            onSelect: () => {
              click('space_resource_reconnect', componentName);
              onClose();
              openChat({
                text: presencePrompt('RECONNECT', row),
                context: { kind: 'resource', intent: 'RECONNECT', resource: { bindingId: row.bindingId, provider: row.provider, name: row.name, kind: row.kind } },
              });
            },
          };
        case 'SET_MAIN':
          return {
            key: action,
            label,
            onSelect: () => {
              click('space_resource_set_main', componentName);
              void run({ action: 'SET_MAIN', bindingId: row.bindingId });
            },
          };
        case 'MOVE_TO_SPACE':
          return {
            key: action,
            label,
            onSelect: () => {
              click('space_resource_move', componentName);
              setMoving(row);
            },
          };
        case 'UNBIND':
        default:
          return {
            key: action,
            label,
            destructive: true,
            onSelect: () => {
              click('space_resource_unbind', componentName);
              void (async () => {
                const ok = await confirm({
                  title: label,
                  message: row.name,
                  confirmLabel: label,
                  destructive: true,
                  bi: { page_name: pageName, modal_name: 'space_resource_unbind' },
                });
                if (ok) await run({ action: 'UNBIND', bindingId: row.bindingId });
              })();
            },
          };
      }
    });

  const actionsOf = (row: ManageRow) =>
    row.actions.map((action) =>
      action.kind === 'CHANGE' || action.kind === 'CONNECT' || action.kind === 'CONNECT_DOMAIN' ? (
        <TextButton key={action.kind} size="small" className={styles.link} disabled={busy} onClick={() => act(row, action)}>
          {t(action.labelKey)}
        </TextButton>
      ) : (
        <Button
          key={action.kind}
          size="small"
          variant={action.primary ? 'contained' : 'outlined'}
          color={action.primary ? 'accent' : 'standard'}
          disabled={busy}
          onClick={() => act(row, action)}
        >
          {t(action.labelKey)}
        </Button>
      ),
    );

  const detailLine = (row: ManageRow) =>
    row.details.length > 0 && (
      <Typography variant="p3" color="secondary" component="span" noWrap className={row.tone ? TONE_CLASS[row.tone] : undefined}>
        {row.details.map(partText).join(' · ')}
      </Typography>
    );

  const menu = (row: ManageRow) => {
    const items = menuFor(row);
    return items.length > 0 ? <OverflowMenu actions={items} ariaLabel={row.name} /> : null;
  };

  const rowView = (row: ManageRow, sectionId: string) => {
    const key = row.bindingId || row.name;
    if (sectionId === 'suggestions') {
      return (
        <div key={key} className={styles.suggestion} data-testid={`presenceSuggestion-${row.bindingId}`}>
          <span className={styles.glyph}>
            <PresenceGlyph glyph={row.glyph} size={16} />
          </span>
          <Typography variant="p2" color="primary" component="span" className={styles.grow}>
            {row.kind === 'WEBSITE'
              ? t('workspace.siteBar.candidateQuestion', { name: row.name })
              : t('workspace.presence.suggestionQuestion', { name: row.name })}
          </Typography>
          {actionsOf(row)}
        </div>
      );
    }
    if (sectionId === 'agents') {
      return (
        <div key={key} className={styles.agentRow}>
          <Typography variant="p3" color="secondary" component="span" className={styles.agentLabel}>
            {row.labelKey ? t(row.labelKey) : ''}
          </Typography>
          <span className={styles.text}>
            <Typography variant="p2" color="primary" component="span" noWrap>
              {row.name}
            </Typography>
            {detailLine(row)}
          </span>
          {actionsOf(row)}
          {menu(row)}
        </div>
      );
    }
    const isWebsite = sectionId === 'websites';
    return (
      <div key={key} className={isWebsite ? styles.websiteRow : styles.row} data-testid={`presenceRow-${row.bindingId}`}>
        {isWebsite ? (
          <span className={styles.thumb}>
            {row.screenshotUrl ? <img src={row.screenshotUrl} alt="" className={styles.thumbImg} loading="lazy" /> : <Globe size={18} aria-hidden />}
          </span>
        ) : (
          <span className={styles.glyph}>
            <PresenceGlyph glyph={row.glyph} size={16} />
          </span>
        )}
        <span className={styles.text}>
          <span className={styles.nameLine}>
            <Typography variant="p2" color="strong" component="span" noWrap className={styles.name}>
              {row.name}
            </Typography>
            {row.roleKey && <Badge label={t(row.roleKey)} className={styles.badge} />}
          </span>
          {detailLine(row)}
        </span>
        <span className={styles.actions}>
          {actionsOf(row)}
          {menu(row)}
        </span>
      </div>
    );
  };

  /** The websites section with the main row drawn as the checklist card (and its ⋯), the other websites under it. */
  const websitesWithCard = (rows: ManageRow[]) => {
    const main = rows[0]?.roleKey === 'workspace.resources.main' ? rows[0] : undefined;
    const rest = main ? rows.slice(1) : rows;
    return (
      <>
        {card && <PresenceChecklistCard card={card} onAct={actOnChecklist} menu={main ? menu(main) : undefined} />}
        {rest.length > 0 && (
          <Card variant="outline" size="sm" className={styles.group}>
            {rest.map((row) => rowView(row, 'websites'))}
          </Card>
        )}
      </>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('workspace.presence.title')}
        headerActions={
          <Typography variant="p3" color="secondary" component="span" noWrap>
            {workspaceName}
          </Typography>
        }
        width={760}
        maxHeightPx={650}
        scrollBody
      >
        <div className={styles.body} data-testid="presenceManage">
          <Typography variant="p2" color="primary" component="p" className={styles.subtitle}>
            {t('workspace.presence.subtitle')}
          </Typography>
          {card && !sections.some((s) => s.id === 'websites') && (
            <section className={styles.section} data-testid="presenceSection-websites">
              <Typography variant="p3" color="secondary" component="h5" className={styles.sectionTitle}>
                {t('workspace.presence.sectionWebsites')}
              </Typography>
              <PresenceChecklistCard card={card} onAct={actOnChecklist} />
            </section>
          )}
          {sections.map((section) => (
            <section key={section.id} className={styles.section} data-testid={`presenceSection-${section.id}`}>
              {section.id !== 'suggestions' && (
                <Typography variant="p3" color="secondary" component="h5" className={styles.sectionTitle}>
                  {t(section.titleKey)}
                </Typography>
              )}
              {section.declinedKey ? (
                <Typography variant="p3" color="secondary">
                  {t(section.declinedKey)}
                </Typography>
              ) : section.id === 'suggestions' ? (
                section.rows.map((row) => rowView(row, section.id))
              ) : section.id === 'websites' && card ? (
                websitesWithCard(section.rows)
              ) : (
                <Card variant="outline" size="sm" className={styles.group}>
                  {section.rows.map((row) => rowView(row, section.id))}
                </Card>
              )}
            </section>
          ))}
          <div className={styles.add}>
            {adding ? (
              <form
                className={styles.addForm}
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitLink();
                }}
              >
                <Input
                  autoFocus
                  type="url"
                  inputMode="url"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={draft}
                  placeholder={t('workspace.presence.addLinkPlaceholder')}
                  aria-label={t('workspace.presence.addLink')}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setAddError(null);
                  }}
                  status={addError ? 'error' : 'default'}
                  statusMessage={addError ?? undefined}
                  className={styles.addInput}
                />
                <Button type="submit" size="large" loading={busy}>
                  {t('workspace.presence.addLinkSubmit')}
                </Button>
              </form>
            ) : (
              <>
                <Button variant="outlined" startIcon={<Plus size={16} aria-hidden />} onClick={() => setAdding(true)} data-testid="presenceAddLink">
                  {t('workspace.presence.addLink')}
                </Button>
                <Typography variant="p3" color="secondary" component="span">
                  {t('workspace.presence.addLinkHint')}
                </Typography>
              </>
            )}
          </div>
        </div>
        <Toast open={toast !== null} message={toast ?? ''} severity="error" onClose={() => setToast(null)} />
      </Modal>
      <Modal open={moving !== null} onClose={() => setMoving(null)} title={t('workspace.resources.moveTitle')} width={440} maxHeightPx={650} scrollBody>
        <div className={styles.moveList}>
          {others.map((ws) => (
            <Button
              key={ws.id}
              variant="text"
              color="standard"
              fullWidth
              className={styles.moveOption}
              startIcon={<WorkspaceBadge ws={ws} size={24} />}
              disabled={busy}
              onClick={() => {
                const row = moving;
                setMoving(null);
                logPageClickBi({ page_name: pageName, button_name: 'space_resource_move_target' });
                if (row) void run({ action: 'MOVE_TO_SPACE', bindingId: row.bindingId, targetWorkspaceId: ws.id });
              }}
            >
              {ws.name}
            </Button>
          ))}
        </div>
      </Modal>
    </>
  );
}
